import { useState, useMemo, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Avatar,
  Empty,
  ConfigProvider,
  Drawer,
  Form,
  Checkbox,
  Switch,
  Divider,
  Skeleton,
  message,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  DashboardOutlined,
  EditOutlined,
  ExperimentOutlined,
  WalletOutlined,
  LoginOutlined,
  TeamOutlined,
  FireOutlined,
  TableOutlined,
  DeleteOutlined,
  InboxOutlined,
  BellOutlined,
  RiseOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { securePath } from "../../../utils/secureRoutes";
import axios from "../../../utils/axiosDash";

export const MODULES = [
  { key: "dashboard", label: "Dashboard", icon: <DashboardOutlined /> },
  { key: "writer", label: "Writer", icon: <EditOutlined /> },
  { key: "hygiene_safety", label: "Hygiene & Safety", icon: <ExperimentOutlined /> },
  { key: "cash_counter", label: "Cash Counter", icon: <WalletOutlined /> },
  { key: "store_entry", label: "Store Entry", icon: <LoginOutlined /> },
  { key: "dining_footfall", label: "Dining Area Footfall", icon: <TeamOutlined /> },
  { key: "kabab_monitoring", label: "Kabab Monitoring", icon: <FireOutlined /> },
  { key: "table_occupancy", label: "Table Occupancy", icon: <TableOutlined /> },
  { key: "wastage_monitoring", label: "Wastage Monitoring", icon: <DeleteOutlined /> },
  { key: "material_inwards", label: "Material Inwards", icon: <InboxOutlined /> },
  { key: "alerts", label: "Alerts", icon: <BellOutlined /> },
  { key: "roi", label: "ROI", icon: <RiseOutlined /> },
];

const MODULE_MAP = Object.fromEntries(MODULES.map((m) => [m.key, m]));
const moduleLabel = (key) => MODULE_MAP[key]?.label || key;

/* ---------- Helpers ---------- */
const AVATAR_TONES = [
  { bg: "#e0f2fe", fg: "#0369a1" },
  { bg: "#d1fae5", fg: "#047857" },
  { bg: "#ede9fe", fg: "#6d28d9" },
  { bg: "#fef3c7", fg: "#b45309" },
  { bg: "#ffe4e6", fg: "#be123c" },
];
const toneFor = (id = "") => {
  const n = String(id).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_TONES[n % AVATAR_TONES.length];
};
const initials = (u) => {
  const a = (u.firstName || u.username || "?")[0] || "";
  const b = (u.lastName || "")[0] || "";
  return `${a}${b}`.toUpperCase();
};
const fullName = (u) => `${u.firstName} ${u.lastName}`.trim() || u.username;
const formatPhone = (phone) => (phone ? `+91 ${phone}` : "—");

/* ---------- Small pieces ---------- */
const UserAvatar = ({ user, size = 40 }) => {
  const t = toneFor(user.id);
  return (
    <Avatar size={size} style={{ background: t.bg, color: t.fg, fontWeight: 600, flexShrink: 0 }}>
      {initials(user)}
    </Avatar>
  );
};

const NoAccessTag = () => (
  <Tag bordered={false} className="!m-0 !rounded-full !px-3 !bg-slate-100 !text-slate-500">
    No access
  </Tag>
);

const AllAccessTag = () => (
  <Tag bordered={false} color="green" className="!m-0 !rounded-full !px-3">
    All modules
  </Tag>
);

/* Compact version for the table (first 2 modules + "+N more") */
const AccessCell = ({ user }) => {
  const count = user.moduleAccess.length;
  const isAll = count === MODULES.length;
  const preview = user.moduleAccess.slice(0, 2);
  const rest = count - preview.length;

  if (count === 0) return <NoAccessTag />;
  if (isAll) return <AllAccessTag />;

  return (
    <Tooltip
      placement="topLeft"
      title={<div className="max-w-xs">{user.moduleAccess.map(moduleLabel).join(", ")}</div>}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {preview.map((k) => (
          <Tag key={k} bordered={false} color="cyan" className="!m-0 !rounded-full !px-2.5">
            {moduleLabel(k)}
          </Tag>
        ))}
        {rest > 0 && (
          <Tag bordered={false} className="!m-0 !rounded-full !bg-slate-100 !text-slate-600">
            +{rest} more
          </Tag>
        )}
      </div>
    </Tooltip>
  );
};

const StatCard = ({ icon, label, value, tone }) => (
  <div className="flex min-w-0 items-center gap-3 rounded-2xl border border-sky-100 bg-white px-4 py-3 shadow-sm">
    <span
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${tone}`}
    >
      {icon}
    </span>
    <div className="min-w-0">
      <div className="text-xl font-semibold leading-tight text-slate-800">{value}</div>
      <div className="truncate text-xs text-slate-500">{label}</div>
    </div>
  </div>
);

/* One labelled row inside a mobile / tablet card */
const InfoRow = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 text-sm">
    <span className="mt-0.5 text-slate-400">{icon}</span>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="break-words text-slate-700">{children}</div>
    </div>
  </div>
);

/* ---------- Edit drawer ---------- */
const EditUserDrawer = ({ user, open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.resetFields();
      setSelected(user.moduleAccess);
    }
  }, [open, user, form]);

  if (!user) return null;

  const allSelected = selected.length === MODULES.length;
  const toggleModule = (key, checked) =>
    setSelected((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  const toggleAll = (checked) => setSelected(checked ? MODULES.map((m) => m.key) : []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        userId: user.id,
        organizationId: localStorage.getItem("organizationId"),
        moduleAccess: MODULES.map((m) => ({
          moduleKey: m.key,
          moduleName: m.label,
          enabled: selected.includes(m.key),
        })),
      };
      // Only send the password when the person typed one
      if (values.password) payload.password = values.password;

      // TODO: make sure this endpoint name matches your backend
      const res = await axios.post("/edit-empire-user", payload);

      if (res.data?.success) {
        message.success("User updated.");
        onSaved({ ...user, moduleAccess: selected });
        onClose();
      } else {
        message.error(res.data?.message || "Could not update the user.");
      }
    } catch (err) {
      // Form validation errors have errorFields; those are shown inline
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || "Could not update the user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // Full width on small phones, capped at 520px on larger screens
      width="min(520px, 100vw)"
      destroyOnClose
      title={
        <div className="flex items-center gap-3">
          <UserAvatar user={user} size={36} />
          <div className="min-w-0">
            <div className="truncate font-semibold text-slate-800">Edit {fullName(user)}</div>
            <div className="truncate text-xs font-normal text-slate-500">{user.username}</div>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save changes
          </Button>
        </div>
      }
      styles={{ body: { background: "#f8fcff", padding: 16 } }}
    >
      <Form form={form} layout="vertical" requiredMark={false} autoComplete="off">
        {/* Password */}
        <section className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
          <div className="mb-1 flex items-center gap-2 font-semibold text-slate-800">
            <SafetyCertificateOutlined className="text-sky-500" /> Change password
          </div>
          <p className="mb-4 text-xs text-slate-500">
            Leave both fields empty to keep the current password.
          </p>

          <Form.Item
            name="password"
            label="New password"
            rules={[{ min: 6, message: "Use at least 6 characters." }]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-slate-400" />}
              placeholder="Enter new password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={["password"]}
            className="!mb-0"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const pw = getFieldValue("password");
                  if (!pw && !value) return Promise.resolve();
                  if (pw && !value) return Promise.reject(new Error("Confirm the new password."));
                  if (pw !== value) return Promise.reject(new Error("Passwords do not match."));
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-slate-400" />}
              placeholder="Re-enter new password"
            />
          </Form.Item>
        </section>

        {/* Modules */}
        <section className="mt-4 rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                <AppstoreOutlined className="text-sky-500" /> Module access
              </div>
              <p className="text-xs text-slate-500">
                {selected.length} of {MODULES.length} modules enabled
              </p>
            </div>
            <label className="flex shrink-0 items-center gap-2 text-sm text-slate-600">
              All
              <Switch size="small" checked={allSelected} onChange={toggleAll} />
            </label>
          </div>

          <Divider className="!my-3" />

          <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
            {MODULES.map((m) => {
              const checked = selected.includes(m.key);
              return (
                <label
                  key={m.key}
                  className={`flex min-w-0 cursor-pointer items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                    checked
                      ? "border-sky-300 bg-sky-50 text-sky-800"
                      : "border-slate-200 bg-white text-slate-600 hover:border-sky-200"
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onChange={(e) => toggleModule(m.key, e.target.checked)}
                  />
                  <span className={checked ? "text-sky-500" : "text-slate-400"}>{m.icon}</span>
                  <span className="truncate">{m.label}</span>
                </label>
              );
            })}
          </div>
        </section>
      </Form>
    </Drawer>
  );
};

/* ---------- Page ---------- */
export default function UserManagement() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const handleAddUser = () => {
    navigate(securePath("/empirehome/add-user-management", { basePath: "/empirehome" }));
  };

  const getCurrentUsername = () => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.username || "";
    } catch {
        return "";
    }
  };

  const fetchEmpireUsers = async () => {
    try {
        setLoading(true);
        const organizationId = localStorage.getItem("organizationId");

        if (!organizationId) {
        message.error("Organization ID not found. Please log in again.");
        setUsers([]);
        return;
        }

        const response = await axios.post("/fetch-empire-users", {
        organizationId,
        currentUsername: getCurrentUsername(), // logged-in user is excluded by the backend
        });

        if (response.data?.success) {
        const formatted = (response.data.data || []).map((user) => {
            const nameParts = (user.name || "").trim().split(/\s+/);
            return {
            id: user._id,
            username: user.username || "",
            firstName: nameParts[0] || "",
            lastName: nameParts.slice(1).join(" "),
            email: user.email || "",
            phone: user.phoneNumber || "",
            moduleAccess: (user.moduleAccess || [])
                .filter((m) => m.enabled)
                .map((m) => m.moduleKey),
            };
        });
        setUsers(formatted);
        } else {
        setUsers([]);
        message.error(response.data?.message || "Failed to fetch users.");
        }
    } catch (error) {
        console.error("Error fetching Empire users:", error.response?.data || error);
        message.error(error.response?.data?.message || "Unable to fetch users. Please try again.");
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmpireUsers();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        fullName(u).toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.phone.includes(q)
    );
  }, [users, search]);

  const withAccess = users.filter((u) => u.moduleAccess.length > 0).length;
  const fullAccess = users.filter((u) => u.moduleAccess.length === MODULES.length).length;

  const handleSaved = (updated) =>
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));

  const editButton = (u, block = false) => (
    <Button
      icon={<EditOutlined />}
      onClick={() => setEditingUser(u)}
      className={`!border-sky-200 !text-sky-600 ${block ? "w-full" : ""}`}
    >
      Edit
    </Button>
  );

  /* One piece of data per column */
  const columns = [
    {
      title: "Name",
      key: "name",
      width: 220,
      render: (_, u) => (
        <div className="flex items-center gap-3">
          <UserAvatar user={u} />
          <span className="font-medium text-slate-800">{fullName(u)}</span>
        </div>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 160,
      render: (v) => <span className="text-slate-600">{v || "—"}</span>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 160,
      render: (v) => <span className="whitespace-nowrap text-slate-600">{formatPhone(v)}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 240,
      render: (v) => <span className="break-all text-slate-600">{v || "—"}</span>,
    },
    {
      title: "Module access",
      key: "access",
      width: 260,
      render: (_, u) => <AccessCell user={u} />,
    },
    {
      title: "",
      key: "actions",
      align: "right",
      fixed: "right",
      width: 100,
      render: (_, u) => editButton(u),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0ea5e9",
          borderRadius: 10,
          fontFamily: "'Plus Jakarta Sans', 'Segoe UI', system-ui, sans-serif",
          controlHeight: 42,
          colorBorder: "#dbe7f3",
        },
        components: {
          Table: { headerBg: "#f0f9ff", headerColor: "#475569", rowHoverBg: "#f8fcff" },
        },
      }}
    >
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-b from-sky-50/60 to-white px-3 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <div className="mx-auto w-full max-w-7xl">
          {/* Title + Add user */}
          <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-800 sm:text-2xl lg:text-3xl">
                User management
              </h1>
              <p className="mt-1 text-sm text-slate-500 sm:text-base">
                See who can sign in, which modules they can open, and update their access.
              </p>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
              className="w-full sm:w-auto"
            >
              Add user
            </Button>
          </div>

          {/* Summary */}
          <div className="mb-5 grid grid-cols-1 gap-3 min-[480px]:grid-cols-3">
            <StatCard
              icon={<TeamOutlined />}
              label="Total users"
              value={users.length}
              tone="bg-sky-50 text-sky-600"
            />
            <StatCard
              icon={<AppstoreOutlined />}
              label="With module access"
              value={withAccess}
              tone="bg-emerald-50 text-emerald-600"
            />
            <StatCard
              icon={<SafetyCertificateOutlined />}
              label="Access to all modules"
              value={fullAccess}
              tone="bg-violet-50 text-violet-600"
            />
          </div>

          {/* List card */}
          <section className="rounded-2xl border border-sky-100 bg-white p-3 shadow-sm sm:p-5 lg:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined className="text-slate-400" />}
                placeholder="Search by name, username, email or phone"
                className="w-full sm:max-w-sm"
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchEmpireUsers}
                loading={loading}
                className="w-full sm:w-auto"
              >
                Refresh
              </Button>
            </div>

            {/* Large screens: table (scrolls sideways if the screen is a bit narrow) */}
            <div className="hidden lg:block">
              <Table
                rowKey="id"
                columns={columns}
                dataSource={filtered}
                loading={loading}
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 8, hideOnSinglePage: true, showSizeChanger: false }}
                locale={{ emptyText: <Empty description="No users found." /> }}
              />
            </div>

            {/* Phones and tablets: cards (1 column on phones, 2 on tablets) */}
            <div className="lg:hidden">
              {loading && users.length === 0 && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Skeleton active avatar paragraph={{ rows: 3 }} />
                  <Skeleton active avatar paragraph={{ rows: 3 }} />
                </div>
              )}

              {!loading && filtered.length === 0 && <Empty description="No users found." />}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {filtered.map((u) => (
                  <div
                    key={u.id}
                    className="flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-4"
                  >
                    {/* Name */}
                    <div className="flex items-center gap-3">
                      <UserAvatar user={u} size={44} />
                      <div className="min-w-0">
                        <div className="text-xs text-slate-400">Name</div>
                        <div className="break-words font-medium text-slate-800">{fullName(u)}</div>
                      </div>
                    </div>

                    <Divider className="!my-3" />

                    {/* Each piece of data on its own row */}
                    <div className="space-y-3">
                      <InfoRow icon={<UserOutlined />} label="Username">
                        {u.username || "—"}
                      </InfoRow>
                      <InfoRow icon={<PhoneOutlined />} label="Phone">
                        {formatPhone(u.phone)}
                      </InfoRow>
                      <InfoRow icon={<MailOutlined />} label="Email">
                        <span className="break-all">{u.email || "—"}</span>
                      </InfoRow>
                      <InfoRow icon={<AppstoreOutlined />} label="Module access">
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {u.moduleAccess.length === 0 ? (
                            <NoAccessTag />
                          ) : u.moduleAccess.length === MODULES.length ? (
                            <AllAccessTag />
                          ) : (
                            u.moduleAccess.map((k) => (
                              <Tag
                                key={k}
                                bordered={false}
                                color="cyan"
                                className="!m-0 !rounded-full"
                              >
                                {moduleLabel(k)}
                              </Tag>
                            ))
                          )}
                        </div>
                      </InfoRow>
                    </div>

                    <div className="mt-4 pt-1">{editButton(u, true)}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <EditUserDrawer
        user={editingUser}
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        onSaved={handleSaved}
      />
    </ConfigProvider>
  );
}